import { useEffect, useRef } from 'react';
import { animate, stagger } from 'animejs';
import { ChevronDown } from 'lucide-react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

export function HeroSection() {
    const subtitleRef = useRef<HTMLParagraphElement>(null);
    const ctaRef = useRef<HTMLDivElement>(null);
    const reducedMotion = useReducedMotion();

    useEffect(() => {
        if (reducedMotion) return;

        // Stagger title words animation
        animate('.hero-word', {
            translateY: [100, 0],
            opacity: [0, 1],
            duration: 1200,
            ease: 'outExpo',
            delay: stagger(150),
        });

        // Subtitle animation
        if (subtitleRef.current) {
            animate(subtitleRef.current, {
                translateY: [30, 0],
                opacity: [0, 1],
                duration: 800,
                ease: 'outExpo',
                delay: 600,
            });
        }

        // CTA animation
        if (ctaRef.current) {
            animate(ctaRef.current, {
                translateY: [20, 0],
                opacity: [0, 1],
                duration: 600,
                ease: 'outExpo',
                delay: 900,
            });
        }
    }, [reducedMotion]);

    const scrollToContent = () => {
        const nextSection = document.getElementById('detection-section');
        nextSection?.scrollIntoView({ behavior: 'smooth' });
    };

    return (
        <section
            className="relative min-h-screen flex items-center justify-center overflow-hidden blueprint-grid"
            style={{ background: 'var(--dark-bg)' }}
            aria-labelledby="hero-title"
        >
            {/* Ambient flame background */}
            <div className="absolute inset-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute top-1/4 left-1/4 w-96 h-96 rounded-full blur-3xl opacity-30 animate-float"
                    style={{ background: 'var(--fire-red)' }}
                />
                <div
                    className="absolute bottom-1/4 right-1/4 w-72 h-72 rounded-full blur-3xl opacity-20 animate-float"
                    style={{ background: 'var(--fire-orange)', animationDelay: '1s' }}
                />
                <div
                    className="absolute top-1/2 right-1/3 w-48 h-48 rounded-full blur-2xl opacity-15 animate-float"
                    style={{ background: 'var(--fire-yellow)', animationDelay: '2s' }}
                />
            </div>

            {/* Radar Pulse */}
            <div className="absolute inset-0 flex items-center justify-center pointer-events-none" aria-hidden="true">
                <div
                    className="w-64 h-64 rounded-full border-2 opacity-30"
                    style={{
                        borderColor: 'var(--fire-orange)',
                        animation: 'radarPulse 3s cubic-bezier(0.16, 1, 0.3, 1) infinite'
                    }}
                />
            </div>

            {/* Technical Status Labels */}
            <div className="absolute top-8 right-8 tech-label opacity-80" aria-hidden="true">
                <span className="inline-block w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" />
                24/7 Monitoring Active
            </div>
            <div className="absolute bottom-24 left-8 tech-label opacity-80" aria-hidden="true">
                System Status: Operational
            </div>
            <div className="absolute top-1/3 right-12 tech-label opacity-60" aria-hidden="true">
                Last Check: 2m ago
            </div>

            {/* Content */}
            <div className="relative z-10 text-center px-4 max-w-5xl mx-auto">
                <h1 className="text-5xl md:text-7xl font-display font-bold text-white leading-tight mb-8">
                    Environaise <br />
                    <span className="text-[var(--fire-orange)]">Risk Analysis Bureau</span>
                </h1>

                <p className="text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
                    The premier study bureau for engineers and architects. We provide deep-dive risk assessments
                    and compliance reports to ensure your designs meet international safety standards.
                </p>

                <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
                    <a href="/register" className="px-8 py-4 bg-[var(--fire-orange)] text-white rounded-lg font-bold text-lg hover:bg-[#e65a2a] transition-all hover:scale-105 shadow-[0_0_30px_rgba(255,107,53,0.3)]">
                        Request Analysis Project
                    </a>
                    <a href="/login" className="px-8 py-4 border border-white/20 text-white rounded-lg font-bold text-lg hover:bg-white/5 transition-all flex items-center gap-2">
                        View Sample Reports
                    </a>
                </div>
            </div>

            {/* Scroll indicator */}
            <button
                onClick={scrollToContent}
                className="absolute bottom-8 left-1/2 -translate-x-1/2 text-gray-400 hover:text-white transition-colors animate-bounce"
                aria-label="Scroll to learn more"
            >
                <ChevronDown className="w-8 h-8" />
            </button>
        </section >
    );
}

export default HeroSection;
